import React, { useState } from "react";
import { useTranslation } from "../../hooks/useTranslation";

/**
 * Shown when a date search returns nothing.
 *
 * Not every free slot is online — some are held back or released late — so an
 * empty result is a lead worth capturing rather than a dead end. The enquiry
 * goes through the same /api/v1/contact endpoint the contact form uses, which
 * emails the team synchronously, with the searched dates attached so nobody
 * has to retype them.
 */
const AvailabilityEnquiry = ({ pickupDate, dropoffDate }) => {
  const { t } = useTranslation("catalog");
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setStatus("submitting");

    const [firstName, ...rest] = form.name.trim().split(/\s+/);
    const message = [
      "AVAILABILITY ENQUIRY (no vehicles matched this search)",
      "",
      `Pick-up: ${pickupDate || "-"}`,
      `Drop-off: ${dropoffDate || "-"}`,
      "",
      `Message: ${form.message || "-"}`,
    ].join("\n");

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(process.env.REACT_APP_API_KEY && { "X-API-Key": process.env.REACT_APP_API_KEY }),
        },
        body: JSON.stringify({
          firstName,
          // The contact endpoint requires a surname; this form asks for one name.
          lastName: rest.join(" ") || "-",
          email: form.email.trim(),
          phone: form.phone,
          message,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "Failed to send enquiry");
      setStatus("success");
    } catch (err) {
      // Never claim an enquiry was sent when it was not.
      setStatus("error");
    }
  };

  const hasDates = pickupDate && dropoffDate;

  return (
    <div className="availability-enquiry">
      <h3 className="availability-enquiry-title">{t("enquiry.title")}</h3>
      <p className="availability-enquiry-text">
        {hasDates
          ? t("enquiry.textWithDates", { pickupDate, dropoffDate })
          : t("enquiry.text")}
      </p>

      {status === "success" ? (
        <div className="availability-enquiry-success">{t("enquiry.success")}</div>
      ) : (
        <form className="availability-enquiry-form" onSubmit={handleSubmit}>
          <input
            className="availability-enquiry-input"
            name="name"
            value={form.name}
            onChange={onChange}
            placeholder={t("enquiry.name")}
            required
          />
          <input
            className="availability-enquiry-input"
            name="email"
            type="email"
            value={form.email}
            onChange={onChange}
            placeholder={t("enquiry.email")}
            required
          />
          <input
            className="availability-enquiry-input"
            name="phone"
            value={form.phone}
            onChange={onChange}
            placeholder={t("enquiry.phone")}
          />
          <textarea
            className="availability-enquiry-input availability-enquiry-textarea"
            name="message"
            rows={3}
            value={form.message}
            onChange={onChange}
            placeholder={t("enquiry.message")}
          />
          {status === "error" && (
            <div className="availability-enquiry-error">{t("enquiry.error")}</div>
          )}
          <button
            className="availability-enquiry-submit"
            type="submit"
            disabled={status === "submitting"}
          >
            {status === "submitting" ? t("enquiry.submitting") : t("enquiry.submit")}
          </button>
        </form>
      )}
    </div>
  );
};

export default AvailabilityEnquiry;
